import React from 'react'
import { Box, Typography } from '@mui/material'
import { UserPlusIcon } from '@heroicons/react/24/outline'

import { Button } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'

export type InvitedUserSummary = { email: string; userId?: string }

interface InviteUsersSuccessPanelProps {
  invitedUsers: InvitedUserSummary[]
  onRestart: () => void
}

export const InviteUsersSuccessPanel: React.FC<InviteUsersSuccessPanelProps> = ({ invitedUsers, onRestart }) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        maxHeight: '70vh',
        minHeight: 0
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: INTERAX_COLORS.sun
          }}
        >
          <UserPlusIcon className="w-6 h-6" />
        </Box>
        <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '24px', fontWeight: 600 }}>
          {t('facilitator.profile.inviteUsers.successPanelTitle')}
        </Typography>
        <Typography sx={{ color: INTERAX_COLORS.sun, fontSize: '16px', fontWeight: 600 }}>
          {t('facilitator.profile.inviteUsers.successTitle', { count: invitedUsers.length })}
        </Typography>
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '14px' }}>
          {t('facilitator.profile.inviteUsers.successPanelSubtitle')}
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}
      >
        {invitedUsers.map((user, index) => (
          <Box
            component="li"
            key={user.userId || user.email}
            sx={{
              listStyle: 'none',
              padding: '14px 18px',
              borderRadius: '14px',
              border: `1px solid ${INTERAX_COLORS.green}`,
              background: 'linear-gradient(135deg, rgba(46, 204, 113, 0.12), rgba(7, 13, 20, 0.9))'
            }}
          >
            <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600, fontSize: '15px' }}>
              {index + 1}. {user.email}
            </Typography>
            <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: '13px' }}>
              {user.userId
                ? t('facilitator.profile.inviteUsers.successItemWithId', {
                    email: user.email,
                    userId: user.userId
                  })
                : t('facilitator.profile.inviteUsers.successItemInviteSent', { email: user.email })}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="primary" onClick={onRestart}>
          {t('facilitator.profile.inviteUsers.successAddMore')}
        </Button>
      </Box>
    </Box>
  )
}
